import React, { useMemo, useState } from 'react';
import { JOB_TYPES, JOB_TYPE_LABELS, PAY_TYPE_LABELS, moneyShort, suggestBooksJobs, extraInvoiceLines, formatDurationShort, commands, CONTRACT_TYPES, CONTRACT_LABEL, contractTypeOf } from '@battleops/shared';
import { useLocalCollection } from '@battleops/client/src/data/useLocal';
import { useMemberNames, useExtras } from '@battleops/client/src/data/useProfit';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { commitWrites } from '@battleops/client/src/data/commit';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { call } from '@battleops/client/src/session/session';
import Icon from './Icon';
import Combo from './Combo';
import { TagsRow } from './TaskDetail';
import { alert, confirm } from './Dialog';
import s from './Profit.module.css';

// CFO plan phase 1 on the web: what the job makes, both versions, and what the figure is missing.

/** "⚠ 2 things missing" → the list, each with its fix. Nothing at all when the data is good. */
export function TrustNote({ items, onFix }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <div className={s.trust}>
      <button className={s.trustHead} onClick={() => setOpen(!open)} aria-expanded={open}>
        <Icon name="alert-triangle" size={14} />{items.length} thing{items.length === 1 ? '' : 's'} missing, so these numbers are rough
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={13} style={{ marginLeft: 'auto' }} />
      </button>
      {open ? items.map((it) => (
        <div key={it.key} className={s.trustRow}>
          <span>{it.text}</span>
          {onFix && it.fix !== 'receipts' ? <button className="btn ghost sm" onClick={() => onFix(it)}>Fix</button> : null}
        </div>
      )) : null}
    </div>
  );
}

const BASIS = { earned: 'earned (% complete)', booked: 'collected (books)' };

/** Both versions side by side: revenue, job costs, labour, profit, per hour; labour by person underneath. */
export function ProfitCard({ cost }) {
  const names = useMemberNames();
  const c = cost?.cfo;
  if (!c) return null;
  if (c.type === 'personal') return <div className="card faint">Personal job: not counted in profit.</div>;
  const both = (l, a, p, strong, tone) => (
    <div className={s.row} data-strong={strong || undefined}>
      <span className="muted">{l}</span>
      <span className="num" style={tone ? { color: tone(a) } : null}>{a}</span>
      <span className="num" style={tone ? { color: tone(p) } : null}>{p}</span>
    </div>
  );
  const sign = (v) => (String(v).startsWith('−') ? 'var(--c-danger)' : 'var(--c-success)');
  const ph = (v) => (v == null ? '—' : `${moneyShort(v)}/h`);
  return (
    <div className="card" style={{ gridColumn: '1 / -1' }}>
      <div className={s.row} data-head>
        <span className="label">Profit · {c.hours} h {c.type ? `· ${JOB_TYPE_LABELS[c.type]}` : ''}</span>
        <span className="label">Actual (books)</span>
        <span className="label">If paid out <span className="faint">(not booked)</span></span>
      </div>
      {both(`Revenue${c.revenue.basis ? ` · ${BASIS[c.revenue.basis]}` : ''}`, moneyShort(c.revenue.amount), moneyShort(c.revenue.amount))}
      {c.revenue.pct != null ? <div className="faint" style={{ fontSize: 12, margin: '2px 0 6px' }}>{Math.round(c.revenue.pct * 100)}% complete by estimate{c.revenue.contract ? ` of ${moneyShort(c.revenue.contract)}` : ''}</div> : null}
      {both(`Job costs · ${c.jobCostsFrom === 'books' ? 'books job' : 'logged here'}${c.equipment?.total ? ` + equipment ${moneyShort(c.equipment.total)}` : ''}`, `– ${moneyShort(c.jobCosts)}`, `– ${moneyShort(c.jobCosts)}`)}
      {(c.equipment?.lines || []).map((l) => <div key={l.id} className="faint" style={{ fontSize: 12, margin: '2px 0 4px' }}>{l.name}: {l.basis} = {moneyShort(l.amount)}</div>)}
      {both('Labour', `– ${moneyShort(c.labour.actual)}`, `– ${moneyShort(c.labour.paidOut)}`)}
      {c.overhead && (c.overhead.actual || c.overhead.paidOut) ? both(`Overhead · ${moneyShort(c.overheadRate?.actual)} / ${moneyShort(c.overheadRate?.paidOut)} per h`, `– ${moneyShort(c.overhead.actual)}`, `– ${moneyShort(c.overhead.paidOut)}`) : null}
      {both('Profit', moneyShort(c.profit.actual), moneyShort(c.profit.paidOut), true, sign)}
      {both('Profit per hour', ph(c.perHour.profitActual), ph(c.perHour.profitPaidOut), true)}
      {c.marginPct ? both('Margin', `${c.marginPct.actual}%`, `${c.marginPct.paidOut}%`) : null}
      {c.labour.byPerson.length ? (
        <>
          <div className="label" style={{ margin: '14px 0 6px' }}>Labour by person</div>
          {c.labour.byPerson.map((p) => (
            <div key={p.uid} className={s.row}>
              <span>{names[p.uid] || 'Someone'} <span className="faint">· {p.hours} h · {p.rates.payType ? PAY_TYPE_LABELS[p.rates.payType] : 'no pay type'}{p.basis ? ` · ${p.basis}` : ''}{p.effectiveHourly != null && p.rates.payType === 'piece' ? ` = ${moneyShort(p.effectiveHourly)}/h` : ''}{p.rates.onJob ? ' · set for this job' : ''}{p.rates.shadow != null ? ` · shadow ${moneyShort(p.rates.shadowRaw ?? p.rates.shadow)}/h${p.rates.shadowRaw != null && p.rates.shadow !== p.rates.shadowRaw ? ` (${moneyShort(p.rates.shadow)} with employer costs)` : ''}` : ''}</span></span>
              <span className="num">{moneyShort(p.actual)}</span>
              <span className="num">{moneyShort(p.paidOut)}</span>
            </div>
          ))}
        </>
      ) : null}
    </div>
  );
}

/** The project's job type (a label), its tags, and the BattleBooks job it's linked to. */
export function JobSetupCard({ project, onSave, focusRef }) {
  const jobs = useLocalCollection('jobs');
  const equipment = useLocalCollection('equipment');
  const { projects } = useProjects();
  const vocab = useMemo(() => [...new Set(projects.flatMap((p) => p.tags || []))], [projects]);
  const [busy, setBusy] = useState(false);
  const linkedTo = useMemo(() => {
    const m = new Map();
    for (const p of projects) if (p.bookProjectId && p.id !== project.id) m.set(p.bookProjectId, p.name);
    return m;
  }, [projects, project.id]);
  const suggested = useMemo(() => new Set(suggestBooksJobs(project, jobs).map((j) => j.id)), [project.customerName, project.name, jobs]);
  const options = useMemo(() => jobs.filter((j) => j.status !== 'cancelled' || j.id === project.bookProjectId)
    .sort((a, b) => (suggested.has(b.id) - suggested.has(a.id)) || String(b.jobNumber || '').localeCompare(String(a.jobNumber || '')))
    .map((j) => ({ id: j.id, label: `${j.jobNumber ? `${j.jobNumber} · ` : ''}${j.name || j.customerName || 'Job'}`, sub: [j.customerName, linkedTo.has(j.id) ? `linked to ${linkedTo.get(j.id)}` : null].filter(Boolean).join(' · ') })), [jobs, linkedTo, project.bookProjectId]);
  const job = jobs.find((j) => j.id === project.bookProjectId) || null;

  async function createJob() {
    setBusy(true);
    try {
      const r = await call('createProject', {
        name: project.name, customerId: project.customerId || null, customerName: project.customerName || project.name, address: project.address || null,
        status: 'active', leadSource: 'battleops', source: 'battleops',
      });
      onSave({ bookProjectId: r.id, booksetId: null });
    } catch (e) {
      alert({ title: 'Couldn’t create the job', message: e?.message || 'Needs a connection to the books.', tone: 'error' });
    } finally { setBusy(false); }
  }

  return (
    <div className="card" ref={focusRef}>
      <div className="label" style={{ marginBottom: 10 }}>Job</div>
      <div className={s.field}>
        <span className="muted">Type</span>
        <select className="input" value={project.jobType || ''} onChange={(e) => onSave({ jobType: e.target.value || null })}>
          <option value="">Not set</option>
          {JOB_TYPES.map((t) => <option key={t} value={t}>{JOB_TYPE_LABELS[t]}</option>)}
        </select>
      </div>
      <div className={s.field}>
        <span className="muted">Contract</span>
        <select className="input" value={project.contractType || ''} onChange={(e) => onSave({ contractType: e.target.value || null })}>
          <option value="">{contractTypeOf({ ...project, contractType: null }, job) ? `${CONTRACT_LABEL[contractTypeOf({ ...project, contractType: null }, job)]} (automatic)` : 'None'}</option>
          {CONTRACT_TYPES.map((t) => <option key={t} value={t}>{CONTRACT_LABEL[t]}</option>)}
        </select>
      </div>
      <BidLink project={project} />
      <div className={s.field} data-field="tags">{/* the project header's "+ tag" lands here (Project.jsx ?focus=tags) */}
        <span className="muted">Tags</span>
        <div>
          <TagsRow tags={project.tags || []} onChange={(tags) => onSave({ tags })} vocab={vocab} />
          {vocab.length ? <div className="faint" style={{ fontSize: 11.5, marginTop: 4 }}>Used: {vocab.slice(0, 8).map((t) => `#${t}`).join(' ')}</div> : null}
        </div>
      </div>
      {equipment.filter((e) => e.unit === 'hour').length ? (
        <div className={s.field}>
          <span className="muted">Tools on this job</span>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {equipment.filter((e) => e.unit === 'hour').map((e) => {
              const on = (project.equipment || []).includes(e.id);
              return <button key={e.id} className={`chip${on ? ' on' : ''}`} onClick={() => onSave({ equipment: on ? (project.equipment || []).filter((x) => x !== e.id) : [...(project.equipment || []), e.id] })}>{e.name}</button>;
            })}
          </div>
        </div>
      ) : null}
      <div className={s.field}>
        <span className="muted">BattleBooks job</span>
        <div>
          <Combo options={options} value={project.bookProjectId || null} icon="briefcase" placeholder="Link to a books job…" emptyText="No jobs published from the books."
            onChange={(o) => { if (o?.id) onSave({ bookProjectId: o.id, booksetId: jobs.find((j) => j.id === o.id)?.booksetId || null }); }} />
          <div className="row" style={{ marginTop: 6, gap: 8 }}>
            {project.bookProjectId ? <button className="btn ghost sm" onClick={() => onSave({ bookProjectId: null, booksetId: null })}><Icon name="x" size={12} />Unlink</button>
              : <button className="btn ghost sm" disabled={busy} onClick={createJob}><Icon name="plus" size={12} />{busy ? 'Creating…' : 'Create a job in the books'}</button>}
            {job ? <span className="faint" style={{ fontSize: 12 }}>{job.contractValue ? `contract ${moneyShort(job.contractValue)}` : 'no contract value'} · paid {moneyShort(job.paid || 0)}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/** One line for a project card: profit per hour (if paid out first), with the actual when it differs. */
export function profitLine(cost) {
  const c = cost?.cfo;
  if (!c || c.type === 'personal' || !(c.hours > 0)) return null;
  // Work that isn't meant to earn (internal), or whose type isn't set, has a cost per hour — not a profit.
  if (!c.type || c.type === 'internal') {
    const per = c.perHour.costPaidOut;
    return per ? { main: `${moneyShort(per)}/h`, label: 'cost', sub: c.type ? null : 'no job type yet', bad: false, margin: null } : null;
  }
  const p = c.perHour.profitPaidOut; const a = c.perHour.profitActual;
  if (p == null) return null;
  return { main: `${moneyShort(p)}/h`, label: 'profit', sub: a !== p ? `${moneyShort(a)}/h actual` : null, bad: p < 0, margin: c.marginPct?.paidOut ?? null };
}

/**
 * The clock bar's money chip: this job's profit per hour so far (if paid out — "what is this hour worth?")
 * and what your hour costs. Tap → both versions and anything the figure is missing.
 */
export function HourChip({ project, me, rates, cost }) {
  const [open, setOpen] = useState(false);
  const c = cost?.cfo;
  if (!project || !c || !c.type || c.type === 'personal' || c.type === 'internal' || !(c.hours > 0) || c.perHour.profitPaidOut == null) return null;
  const p = c.perHour.profitPaidOut;
  const mine = c.labour.byPerson.find((x) => x.uid === me)?.rates || rates || null;
  return (
    <span style={{ position: 'relative' }}>
      <button className={s.chip} data-bad={p < 0 || undefined} onClick={() => setOpen(!open)} title="Profit per hour on this job so far, if everyone’s time were paid out">
        <Icon name="trending-up" size={12} />{moneyShort(p)}/h{mine ? <span className="faint"> · you cost {moneyShort(mine.paidOut)}/h</span> : null}{cost.trust?.length ? <span style={{ color: 'var(--c-warning)' }}> ⚠</span> : null}
      </button>
      {open ? (
        <div className={s.pop} onMouseLeave={() => setOpen(false)}>
          <div className={s.row} data-head><span className="label">{project.name}</span><span className="label">Actual</span><span className="label">Paid out</span></div>
          <div className={s.row}><span className="muted">Profit / h</span><span className="num">{moneyShort(c.perHour.profitActual)}</span><span className="num">{moneyShort(p)}</span></div>
          {mine ? <div className={s.row}><span className="muted">Your hour costs</span><span className="num">{moneyShort(mine.actual)}</span><span className="num">{moneyShort(mine.paidOut)}</span></div> : null}
          <div className={s.row}><span className="muted">Profit so far</span><span className="num">{moneyShort(c.profit.actual)}</span><span className="num">{moneyShort(c.profit.paidOut)}</span></div>
          <div className="faint" style={{ fontSize: 11.5, marginTop: 6 }}>{c.hours} h on the job · paid out = shadow pay counted, never booked</div>
          {cost.trust?.length ? <div style={{ marginTop: 8, fontSize: 12, color: 'var(--c-warning)' }}>{cost.trust.map((t) => <div key={t.key}>⚠ {t.text}</div>)}</div> : null}
        </div>
      ) : null}
    </span>
  );
}

/** Link the bid this job was sold on: its labour and cost budgets come onto the project (BattleBids writes them). */
function BidLink({ project }) {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const b = project.bidBudget || null;
  async function open() {
    setBusy(true);
    try { const r = await call('opsListEstimates', {}); setList(r?.estimates || []); } catch (e) { alert({ title: 'Couldn’t load the bids', message: e?.message || 'Needs a connection.', tone: 'error' }); } finally { setBusy(false); }
  }
  async function link(e) {
    setBusy(true);
    try { await call('opsImportEstimate', { booksetId: e.booksetId, estimateId: e.estimateId, opsProjectId: project.id, budgetOnly: true }); setList(null); } catch (er) { alert({ title: 'Couldn’t link the bid', message: er?.message || 'Needs a connection.', tone: 'error' }); } finally { setBusy(false); }
  }
  const options = (list || []).map((e) => ({ id: e.estimateId, label: `${e.number ? `${e.number} · ` : ''}${e.title || 'Bid'}`, sub: e.customerName, e }));
  return (
    <div className={s.field}>
      <span className="muted">Bid</span>
      {list ? <Combo options={options} value={b?.estimateId || null} placeholder="Pick the bid…" onChange={(id) => { const o = options.find((x) => x.id === id); if (o) link(o.e); }} />
        : <button className="btn sm" disabled={busy} onClick={open}><Icon name="file-text" size={13} />{b ? `${b.number || 'Linked'} · ${moneyShort(Math.round(b.labour.sell / b.labour.hours))}/h sold` : 'Link a bid'}</button>}
    </div>
  );
}

/** A job's extras: what each is worth at the rate, what's left to bill, and a draft invoice for them on the linked books job. */
export function ExtrasCard({ project, cost }) {
  const x = useExtras(project);
  const { tasks } = useTasks();
  const [busy, setBusy] = useState(false);
  if (!x?.rows.length) return null;
  const job = cost?.job || null;
  const lines = extraInvoiceLines(x.rows, x.rate);
  async function bill() {
    if (!job || !project.customerId) { alert({ title: 'Link a job first', message: job ? 'This project needs a customer.' : 'This project needs a books job and a customer.', tone: 'error' }); return; }
    if (!(await confirm({ title: 'Bill extras', message: `Draft invoice: ${lines.length} extra${lines.length === 1 ? '' : 's'}, ${moneyShort(x.toBill)} before GST. Nothing is sent.`, confirmLabel: 'Make draft' }))) return;
    setBusy(true);
    try {
      const r = await call('createInvoice', { draft: true, kind: 'change-order', source: 'battleops', customerId: project.customerId, projectId: job.id, projectName: job.name || project.name, projectNumber: job.jobNumber || undefined, taxRate: 0.05, lineItems: lines });
      if (r?.pendingApproval || r?.held) { alert({ title: 'Waiting for approval', message: 'Nothing is billed until it’s approved.' }); return; }
      const invoiceId = r?.id || r?.invoiceId;
      if (!invoiceId) throw new Error('No invoice came back.');
      const ids = x.rows.filter((row) => row.ms > 0 && !row.billed).map((row) => row.task.id);
      await commitWrites(commands.markExtrasBilled({ tasks, now: Date.now }, { ids, invoiceId }).writes);
    } catch (e) {
      alert({ title: 'Couldn’t make the draft', message: e?.message || 'Needs a connection to the books.', tone: 'error' });
    } finally { setBusy(false); }
  }
  return (
    <div className="card">
      <div className="label" style={{ marginBottom: 10 }}>Extras</div>
      <div className={s.extrasTop}><b className="num">{x.rate ? moneyShort(x.toBill) : 'No rate set'}</b><span className="muted">{x.rate ? `to bill · ${formatDurationShort(x.toBillMs)} at ${moneyShort(x.rate)}/h` : formatDurationShort(x.toBillMs)}</span></div>
      {x.rows.map((r) => (
        <div key={r.task.id} className={s.extrasRow}>
          <span className={s.extrasTitle} title={r.task.title}>{r.task.title}</span>
          {r.billed ? <span className="faint">Billed</span> : r.clientOk === false ? <span style={{ color: 'var(--c-warning)' }}>Not yet</span> : null}
          <span className="num">{r.value != null ? moneyShort(Math.round(r.value)) : formatDurationShort(r.ms)}</span>
        </div>
      ))}
      <button className="btn sm" style={{ marginTop: 10 }} disabled={busy || !lines.length} onClick={bill}><Icon name="file-text" size={13} />Bill extras</button>
    </div>
  );
}
